/**
 * Does this item take one code per colour, or two?
 *
 * LSK asked the question that exposed the bug: *"if BFJOY152(12)FB1AB was never separated into A
 * and B before colour, it will generate the AB colour — it won't generate A colour and B colour,
 * right?"* Correct. And the first version got it wrong, because it decided from a trailing `AB`.
 *
 *   item is SPLIT      → two boxes carry two tags → a code per colour PER PIECE, mapped as splits
 *   item is NOT split  → one box carries one tag  → ONE code per colour, ending `AB`, as colours
 *
 * The suffix cannot answer it. Of 1,467 accounting items ending in `AB`, only **924** are split;
 * `DV2DWHD15219012FB1AB` is not, and already carries 164 single-code colours. `DRMAB4K0092AB` ends
 * in `AB` because those letters are part of the code.
 *
 * Getting it wrong is not cosmetic. Generating pieces for an unsplit item invents products that no
 * tag will ever carry, and writes them to `map_sku_split`, where quantities PAIR — so a scan of one
 * box would resolve to zero complete sets and sit as an orphan forever.
 *
 * A MIXED batch is refused rather than guessed: one upload carries one format, and choosing
 * silently would write half the rows into the wrong table.
 */

import { setPieceCodes, type SetPieceRow } from './setPieceCodes.js';

export type ItemSplitStatus = {
  itemCode: string;
  isSplit: boolean;
  /** Does the accounting description say this is a two-box set? See `looksLikeSet`. */
  looksLikeSet?: boolean;
};

/**
 * Does the item's DESCRIPTION say it is a two-box set?
 *
 * The code cannot say it; the description can. Across 1,467 accounting items ending in `AB`:
 * 924 carry the marker and are split, 538 carry it and are NOT, 5 are genuinely one box — and
 * **zero** split items lack it. No false negatives, which is what makes it safe to act on.
 *
 * Spacing varies because people typed it: `(A+B)`, ` A+B`, `)A+B`, and `A+ B` are all live.
 * `DRMAB4K0092AB` must NOT match — there the letters are part of a decoration's name.
 */
export function looksLikeSet(description: string | null | undefined): boolean {
  if (description === null || description === undefined) return false;
  return /A\s*\+\s*B/i.test(description);
}

/**
 * The two boxes of a set — the common case, kept as a named shortcut.
 *
 * The derivation itself lives in `setPieceCodes`, which also handles sets of more than two pieces
 * and codes that do not end with their pieces. One rule, not two that can drift apart.
 */
export function baseSetPieces(itemCode: string): SetPieceRow[] {
  return setPieceCodes(itemCode, 'AB');
}

export type FamilyKind = {
  kind: 'set' | 'colour' | 'mixed' | 'needs-split' | 'unknown';
  /** Shown on the screen so the choice is visible rather than mysterious. */
  reason: string;
  split: string[];
  notSplit: string[];
  /** Genuine sets that were never separated. They must be split BEFORE their colours are made. */
  needsSplit: string[];
};

export function decideFamilyKind(items: ItemSplitStatus[]): FamilyKind {
  const split = items.filter((i) => i.isSplit).map((i) => i.itemCode);
  const notSplit = items.filter((i) => !i.isSplit).map((i) => i.itemCode);
  // A genuine set that nobody has separated yet. Colouring it now would produce one code per
  // colour for a product that ships in two boxes — codes no tag on a separated set can match.
  const needsSplit = items
    .filter((i) => !i.isSplit && i.looksLikeSet === true)
    .map((i) => i.itemCode);

  if (items.length === 0) {
    return { kind: 'unknown', reason: 'no items entered yet', split, notSplit, needsSplit };
  }

  // Checked FIRST: it is actionable and it outranks the mixed report, which would otherwise just
  // say "do them separately" without saying that one group is not ready at all.
  if (needsSplit.length > 0) {
    return {
      kind: 'needs-split',
      reason:
        `${needsSplit.length} of these are two-box sets that have not been separated into A and B ` +
        `yet. Separate them first — their colours need a code per box.`,
      split,
      notSplit,
      needsSplit,
    };
  }
  if (split.length > 0 && notSplit.length > 0) {
    return {
      kind: 'mixed',
      reason:
        `${split.length} of these are split into pieces and ${notSplit.length} are not, so they ` +
        `need different formats. Do them as two batches.`,
      split,
      notSplit,
      needsSplit,
    };
  }
  if (notSplit.length === 0) {
    return {
      kind: 'set',
      reason: 'already split into pieces — each colour needs a code per box',
      split,
      notSplit,
      needsSplit,
    };
  }
  return {
    kind: 'colour',
    reason: 'not split into pieces — one code per colour, covering the whole item',
    split,
    notSplit,
    needsSplit,
  };
}
